import {
  CheckoutPaymentOptionsOtherThanPayPal,
  PaymentButtonType
} from '@client/components/buttons/types';
import {
  ButtonVariation,
  type ButtonStyleConfig,
  type HostedButtonDetails,
  type PaymentAmountDetails,
  type PaymentBreakdownAmount,
  type SDKButtonStyle,
  type UcpConfig,
  FieldValueContent,
  type UserInputsType,
  MerchantInfo
} from '@client/typings/Common';
import { WorldReadyProps } from '@client/contexts/WorldReadyContext';
import { getButtonVariablesFromMerchantSettings } from '@components/Settings/utils';
import type { ButtonConfig, DiscountType } from '@client/components/buttons/types';
import { SecondButtonType, Stacked3ButtonPaymentOption } from '@components/Settings/types';
import { IAppContext } from '@client/contexts/AppContext';
import { supportedCurrencies } from '@common/currencyConfigs';
import { getFeatureFlags } from '@client/helpers/ucpConfig';
import { JSSDK_SPECIAL_LOCALES_MAP } from '@common/constants';
import { extractSourceAndFlowType } from '@common/fpti';
import { FlowVariationType } from '@common/analytics';
import { PREVIEW_CART_ITEM_ID } from '@client/utils/constants';
import { getDiscount, hasDiscount } from '@common/utils';
import {
  CurrencyConfigType,
  formatCurrency,
  getCurrencyConfig,
  getLocalizedPercentage,
  getLocalizedString
} from './localeHelper';
import { DEFAULT_MERCHANT_CHECKOUT_SETTINGS } from '../components/Settings/getInitialMerchantSettings';

const ENABLED_FUNDING_METHODS = [
  CheckoutPaymentOptionsOtherThanPayPal.Venmo,
  CheckoutPaymentOptionsOtherThanPayPal.PayLater
];

export const transformButtonVariables = (linkVariables: HostedButtonDetails['link_variables']) =>
  (linkVariables || []).reduce((acc, curr) => {
    acc[curr.name] = curr.value;
    return acc;
  }, {} as Record<string, string>);

export const transformButtonVariablesToLinkVariables: (
  buttonVariables: Record<string, string>
) => Array<{ name: string; value: string }> = (buttonVariables) =>
  Object.entries(buttonVariables || {}).reduce(
    (acc, [name, value]) => [...acc, { name, value }],
    []
  );

export const transButtonStyleFromButtonVariables = (
  buttonStyleConfig: ButtonStyleConfig
): SDKButtonStyle => {
  const { layout, shape, color, button_text: label, tagline } = buttonStyleConfig;
  const style: SDKButtonStyle = { layout, shape, color, label };

  if (layout !== 'vertical') {
    style.tagline = tagline === 'true' || tagline === true;
  } else {
    // JSSDK - style.tagline is not allowed for vertical layout
    style.tagline = false;
  }
  return style;
};

export const isMerchantHostedExp = () =>
  typeof window !== 'undefined' && window.opener && window.opener !== window;

/**
 * Align with round method used in worldready
 * (2.54, 0) => 3
 * (2.54, 1) => 2.5
 * (2.54, 2) => 2.54
 * (2.54, 3) => 2.54
 */
export const roundToMaxDecimal = (initValue: number, maximumFractionDigits: number = 2) => {
  const maxDecimals = Math.max(0, maximumFractionDigits);
  return +`${Math.round(Number(`${initValue}e${maxDecimals}`))}e-${maxDecimals}`;
};

export const getPercentAmount = (
  price: number,
  percent: number,
  maximumFractionDigits: number = 2
) => {
  const percentAmt = (price * percent) / 100;
  return roundToMaxDecimal(percentAmt, maximumFractionDigits);
};

export const getDiscountInfo = (
  itemPrice: number,
  worldReady: WorldReadyProps,
  discountAmount?: string,
  discountRate?: string,
  discountType?: string,
  currencyCode?: string
) => {
  let discountInfo;
  let additionalDiscountInfo;
  if (hasDiscount(discountAmount, discountRate)) {
    // discountValue is negative
    const discountValue = getDiscount(
      Number(itemPrice) || 0,
      discountAmount,
      discountRate,
      discountType,
      getCurrencyConfig(worldReady, currencyCode).maxDecimalPlaces,
      false
    );
    if (discountValue !== 0) {
      const formattedDiscount = formatCurrency(worldReady, currencyCode, discountValue * -1, true);
      const discountedItemPrice = formatCurrency(
        worldReady,
        currencyCode,
        Number(itemPrice) + discountValue,
        true
      );
      if (discountAmount) {
        additionalDiscountInfo = getLocalizedString(worldReady, 'payment.discount.flatApplied', {
          flatAmount: formatCurrency(worldReady, currencyCode, discountAmount, true)
        });
      } else {
        additionalDiscountInfo = getLocalizedString(worldReady, 'payment.discount.rateApplied', {
          percentage: getLocalizedPercentage(worldReady, discountRate),
          discountAmountFromRate: formattedDiscount
        });
      }

      discountInfo = {
        discountedItemPrice: discountedItemPrice ?? '',
        discountRate: getLocalizedPercentage(worldReady, discountRate),
        formattedDiscount: formattedDiscount ?? '',
        additionalDiscountInfo
      };
    }
  }
  return discountInfo;
};

export const buttonIsEligible = (
  paymentMethod: CheckoutPaymentOptionsOtherThanPayPal,
  country: string | undefined,
  currency: string,
  config: UcpConfig | undefined
) => {
  const paymentMethodConfig = config?.[paymentMethod];
  if (paymentMethodConfig) {
    const isSupportedCountry =
      !paymentMethodConfig.supportedCountries ||
      (country && paymentMethodConfig.supportedCountries.includes(country));
    const isSupportedCurrency =
      !paymentMethodConfig.supportedCurrencies ||
      paymentMethodConfig.supportedCurrencies.includes(currency);
    return isSupportedCountry && isSupportedCurrency;
  }
  return true;
};

export function isKeyInObject<T extends object>(key: unknown, obj: T): key is keyof T {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(obj, key);
}

export const buttonIsAvailable = (
  paymentMethod: CheckoutPaymentOptionsOtherThanPayPal,
  country: string | undefined,
  currency: string,
  globalConfig: UcpConfig | undefined,
  flags: { enableApplePay: boolean },
  profileShipTax?: boolean
) => {
  if (paymentMethod === CheckoutPaymentOptionsOtherThanPayPal.ApplePay && !flags.enableApplePay) {
    return false;
  }
  if (
    paymentMethod === CheckoutPaymentOptionsOtherThanPayPal.Venmo &&
    profileShipTax &&
    !globalConfig?.featureFlags?.venmoShipTaxSupport
  ) {
    return false;
  }
  const paymentMethodConfig = globalConfig?.[paymentMethod];
  const paymentMethodEnabled = !!paymentMethodConfig?.enabled;
  const buttonEligible = buttonIsEligible(paymentMethod, country, currency, globalConfig);
  if (!paymentMethodEnabled || !buttonEligible) {
    return false;
  }
  return true;
};

export const transSDKFundingConfig = (
  allowedPaymentMethods: `${CheckoutPaymentOptionsOtherThanPayPal}`[]
) => {
  const disabledPaymentMethods = Object.values(CheckoutPaymentOptionsOtherThanPayPal).filter(
    (method) => !allowedPaymentMethods.includes(method)
  );

  const enabledPaymentMethods = ENABLED_FUNDING_METHODS.filter((method) =>
    allowedPaymentMethods.includes(method)
  );

  const newConfig = {
    enableFunding: enabledPaymentMethods.length > 0 ? enabledPaymentMethods.join(',') : '',
    disableFunding: disabledPaymentMethods.length > 0 ? disabledPaymentMethods.join(',') : ''
  };
  return newConfig;
};

export const getPaymentBreakdown = (
  paymentDetails: PaymentAmountDetails,
  currencyConfig: CurrencyConfigType
): PaymentBreakdownAmount => {
  const maxDecimal = currencyConfig.maxDecimalPlaces;
  let shippingValue = parseFloat(paymentDetails.shipping);
  let taxRateValue = parseFloat(paymentDetails.taxRate);
  let itemPriceValue = parseFloat(paymentDetails.itemPrice);
  let discountValue = paymentDetails.discount;
  let handlingValue = parseFloat(paymentDetails.handling);
  if (Number.isNaN(itemPriceValue)) {
    itemPriceValue = 0;
  }
  if (Number.isNaN(shippingValue)) {
    shippingValue = 0;
  }
  if (Number.isNaN(taxRateValue)) {
    taxRateValue = 0;
  }
  if (Number.isNaN(handlingValue)) {
    handlingValue = 0;
  }
  if (!discountValue || Number.isNaN(discountValue)) {
    discountValue = 0;
  }
  const roundedPrice = roundToMaxDecimal(itemPriceValue, maxDecimal);
  const subtotal = roundedPrice * paymentDetails.quantity;
  const taxAmount = getPercentAmount(subtotal - discountValue, taxRateValue, maxDecimal);
  return {
    subtotal: roundToMaxDecimal(subtotal, maxDecimal),
    handling: roundToMaxDecimal(handlingValue, maxDecimal),
    shipping: roundToMaxDecimal(shippingValue, maxDecimal),
    taxAmount: roundToMaxDecimal(taxAmount, maxDecimal),
    total: roundToMaxDecimal(
      subtotal + handlingValue + shippingValue + taxAmount - discountValue,
      maxDecimal
    )
  };
};

export const getAllowedPaymentMethodsFromConfigs = (
  merchantCountry: string | undefined,
  ucpConfig: UcpConfig | undefined,
  currencyCode: string,
  toggles: {
    paymentMethodVenmo: boolean | undefined;
    paymentMethodPayLater: boolean | undefined;
    paymentMethodApplePay: boolean | undefined;
  },
  flags: {
    enableApplePay: boolean; // TODO: Remove this after elmo is ramped to 100%
  },
  profileShipTax?: boolean
): CheckoutPaymentOptionsOtherThanPayPal[] => {
  const venmoToggle =
    toggles.paymentMethodVenmo ?? DEFAULT_MERCHANT_CHECKOUT_SETTINGS.payment_method_venmo;
  const payLaterToggle =
    toggles.paymentMethodPayLater ?? DEFAULT_MERCHANT_CHECKOUT_SETTINGS.payment_method_paylater;
  const applePayToggle =
    toggles.paymentMethodApplePay ?? DEFAULT_MERCHANT_CHECKOUT_SETTINGS.payment_method_apple_pay;

  const switches = {
    [CheckoutPaymentOptionsOtherThanPayPal.Venmo]: venmoToggle,
    [CheckoutPaymentOptionsOtherThanPayPal.PayLater]: payLaterToggle,
    [CheckoutPaymentOptionsOtherThanPayPal.ApplePay]: applePayToggle,
    [CheckoutPaymentOptionsOtherThanPayPal.Card]: true
  };
  const allowedMethods = Object.values(CheckoutPaymentOptionsOtherThanPayPal).reduce(
    (acc, paymentOption) => {
      const buttonAvailable = buttonIsAvailable(
        paymentOption,
        merchantCountry,
        currencyCode,
        ucpConfig,
        flags,
        profileShipTax
      );
      if (buttonAvailable && switches[paymentOption]) {
        return acc.concat(paymentOption);
      }
      return acc;
    },
    [] as CheckoutPaymentOptionsOtherThanPayPal[]
  );
  return allowedMethods;
};

export const shouldShow3ButtonsInDetails = (appContext: IAppContext | null) => {
  const originalButtonVariables = transformButtonVariables(
    appContext?.hostedButton?.button_variables
  );
  const merchantSettings = appContext?.merchantSettings;
  const buttonVariables = getButtonVariablesFromMerchantSettings(
    merchantSettings,
    originalButtonVariables
  );
  return !!(
    (appContext?.isSlowRampMerchant || !!buttonVariables.checkout_button_type) &&
    getFeatureFlags(appContext).jsSDKThreeButtonExp
  );
};

export const shouldShow3ButtonsInEditor = (appContext: IAppContext | null) =>
  !!(appContext?.is3ButtonsEligible && getFeatureFlags(appContext).jsSDKThreeButtonExp);

export const get3ButtonsPaymentEligibility = (
  enableApplePay?: boolean,
  currencyCode?: string,
  country?: string,
  config?: UcpConfig,
  profileShipTax?: boolean
) => {
  const flags = { enableApplePay: enableApplePay ?? false };
  const showVenmo = currencyCode
    ? buttonIsAvailable(
        CheckoutPaymentOptionsOtherThanPayPal.Venmo,
        country,
        currencyCode,
        config,
        flags,
        profileShipTax
      ) && window?.paypal?.isFundingEligible?.(CheckoutPaymentOptionsOtherThanPayPal.Venmo)
    : false;
  const showPayLater = currencyCode
    ? buttonIsAvailable(
        CheckoutPaymentOptionsOtherThanPayPal.PayLater,
        country,
        currencyCode,
        config,
        flags
      ) && window?.paypal?.isFundingEligible?.(CheckoutPaymentOptionsOtherThanPayPal.PayLater)
    : false;
  const eligibleApplePay = currencyCode
    ? buttonIsAvailable(
        CheckoutPaymentOptionsOtherThanPayPal.ApplePay,
        country,
        currencyCode,
        config,
        flags
      )
    : false;
  return {
    showVenmo,
    showPayLater,
    eligibleApplePay
  };
};
/**
 * Map WorldReady locale to jssdk locale.
 * @param locale
 * @returns
 */
export const sdkLocaleMapper = (locale: string) => {
  return (JSSDK_SPECIAL_LOCALES_MAP[locale] || locale || 'en-US').replace('-', '_');
};

export const shouldShow3ButtonsAlert = (appContext: IAppContext | null) => {
  const originalButtonVariables = transformButtonVariables(
    appContext?.hostedButton?.button_variables
  );

  const merchantSettings = appContext?.merchantSettings;
  const buttonVariables = getButtonVariablesFromMerchantSettings(
    merchantSettings,
    originalButtonVariables
  );
  return (
    appContext?.hostedButton?.id &&
    shouldShow3ButtonsInEditor(appContext) &&
    !buttonVariables.checkout_button_type
  );
};

export const getSourceAndFlowTypeForOrderStatus = (appContext: IAppContext | null) => {
  const checkoutButtonTextType = appContext?.orderDetails?.checkoutButtonTextType;
  const isSlowRampMerchant = appContext?.isSlowRampMerchant;
  let { sourceType, flowType } = extractSourceAndFlowType(appContext?.entryPoint);
  if (isMerchantHostedExp()) {
    if (isSlowRampMerchant || !!checkoutButtonTextType) {
      flowType = 'merchant';
      sourceType = '3_button_code';
    } else {
      flowType = 'merchant';
      sourceType = 'button_code';
    }
  }
  return { sourceType, flowType };
};

export const getButtonVariation = (appContext: IAppContext | null) =>
  appContext?.buttonVariation || ButtonVariation.BuyNow;

export const getFlowVariation = (appContext: IAppContext | null): FlowVariationType =>
  getButtonVariation(appContext) === ButtonVariation.Cart ? 'add_to_cart' : 'buy_now';

export const getCurrencyList = (country: string | undefined) => {
  return (
    country === 'MY' ? supportedCurrencies : supportedCurrencies.filter((_) => _ !== 'MYR')
  ).map((currency) => ({
    value: currency,
    label: currency
  }));
};

export const getValueContent = (
  worldReady: WorldReadyProps,
  value: number | `${FieldValueContent}`,
  currencyCode: string | undefined,
  showCurrencySymbol: boolean = true
) => {
  if (value === FieldValueContent.EstimateLater) {
    return getLocalizedString(worldReady, 'payment.text.estimatedLater');
  }

  if (value === FieldValueContent.Free) {
    return getLocalizedString(worldReady, 'payment.text.freeShipping');
  }

  return formatCurrency(worldReady, currencyCode, value, showCurrencySymbol);
};

export const getNumItemsInPreviewCart = (userInputs: UserInputsType) =>
  parseInt(userInputs?.cartItems?.[PREVIEW_CART_ITEM_ID]?.quantity || '0', 10);

export const getPreviewMerchantInfoWithFallback = (
  worldReady: WorldReadyProps,
  merchantInfo: MerchantInfo | undefined
): MerchantInfo => ({
  ...merchantInfo,
  businessName:
    merchantInfo?.businessName ||
    `[${getLocalizedString(worldReady, 'app.business.name.fallbackText')}]`,
  countryCode: merchantInfo?.countryCode || '',
  merchantId: merchantInfo?.merchantId || '',
  primaryCurrencyCode: merchantInfo?.primaryCurrencyCode || null
});

export const get3ButtonRenderListFromButtonConfig = (
  buttonConfig: ButtonConfig,
  options: {
    showVenmo: boolean | undefined;
    showPayLater: boolean | undefined;
  }
) => {
  const buttonRenderList = [Stacked3ButtonPaymentOption.PayPal];

  switch (buttonConfig.secondButtonType) {
    case SecondButtonType.PayPalRecommended:
      if (buttonConfig.paymentMethodVenmo && options?.showVenmo) {
        buttonRenderList.push(Stacked3ButtonPaymentOption.Venmo);
      } else if (buttonConfig.paymentMethodPayLater && options?.showPayLater) {
        buttonRenderList.push(Stacked3ButtonPaymentOption.PayLater);
      }
      break;

    case SecondButtonType.Venmo:
      if (buttonConfig.paymentMethodVenmo && options?.showVenmo) {
        buttonRenderList.push(Stacked3ButtonPaymentOption.Venmo);
      }
      break;

    case SecondButtonType.PayLater:
      if (buttonConfig.paymentMethodPayLater && options?.showPayLater) {
        buttonRenderList.push(Stacked3ButtonPaymentOption.PayLater);
      }
      break;

    default:
      break;
  }

  buttonRenderList.push(Stacked3ButtonPaymentOption.Checkout);
  return buttonRenderList;
};

export const get3ButtonPreferencesFromButtonVariables = (
  buttonVariables: Record<string, string | undefined>,
  enableFundingMethods: CheckoutPaymentOptionsOtherThanPayPal[]
) => {
  const buttonPreferences: Array<Stacked3ButtonPaymentOption | 'default'> = [
    Stacked3ButtonPaymentOption.PayPal
  ];

  switch (buttonVariables.second_button_type) {
    case SecondButtonType.PayPalRecommended:
      if (
        buttonVariables.payment_method_venmo === 'true' &&
        enableFundingMethods.includes(CheckoutPaymentOptionsOtherThanPayPal.Venmo)
      ) {
        buttonPreferences.push(Stacked3ButtonPaymentOption.Venmo);
      } else if (
        buttonVariables.payment_method_paylater === 'true' &&
        enableFundingMethods.includes(CheckoutPaymentOptionsOtherThanPayPal.PayLater)
      ) {
        buttonPreferences.push(Stacked3ButtonPaymentOption.PayLater);
      }
      break;

    case SecondButtonType.Venmo:
      if (
        buttonVariables.payment_method_venmo === 'true' &&
        enableFundingMethods.includes(CheckoutPaymentOptionsOtherThanPayPal.Venmo)
      ) {
        buttonPreferences.push(Stacked3ButtonPaymentOption.Venmo);
      }
      break;

    case SecondButtonType.PayLater:
      if (
        buttonVariables.payment_method_paylater === 'true' &&
        enableFundingMethods.includes(CheckoutPaymentOptionsOtherThanPayPal.PayLater)
      ) {
        buttonPreferences.push(Stacked3ButtonPaymentOption.PayLater);
      }
      break;

    default:
      break;
  }

  return buttonPreferences;
};

export const isMemoLabelOptional = (buttonVariables: Record<string, string | undefined>) =>
  buttonVariables.mandate_customer_memo === 'false';

export const isButtonsLabelsOverrideEnabled = (
  paymentButtonType: `${PaymentButtonType}`,
  isSectionToggledOn: boolean
) => isSectionToggledOn && paymentButtonType === PaymentButtonType.Button;

// Taken from pp-react
// https://github.paypal.com/PayPal-UI-R/pp-react/blob/6815c9f62a7e461cc6d38eaac677ad9db0dbc4a2/packages/horizontal-scroll/src/horizontalScroll.tsx#L76-L79
// https://github.paypal.com/PayPal-UI-R/pp-react/search?p=2&q=breakpoints.mediaMd
export const DESKTOP_WIDTH_BREAKPOINT = 752;

export const getIsDesktop = () => {
  return window.innerWidth >= DESKTOP_WIDTH_BREAKPOINT;
};

export const MOBILE_WIDTH_BREAKPOINT = 751;

export const getIsMobile = () => {
  return window.innerWidth <= MOBILE_WIDTH_BREAKPOINT;
};
// We should expand the section by default if there is any toggle inside turned on
export const shouldShowMoreOptionsInEditor = (toggleValues: Array<boolean | undefined>): boolean =>
  toggleValues.some((toggleValue) => toggleValue);
